import {
  type KeyboardEvent,
  type ReactNode,
  forwardRef,
  useId,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';
import {
  Chevron,
  FieldShell,
  SelectField,
  type FieldStatus,
  type SelectOption,
} from '@/components/form-elements/Field';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import VisuallyHidden from '@/components/VisuallyHidden/VisuallyHidden';
import useMediaQuery, { DESKTOP_MEDIA_QUERY } from '@/hooks/useMediaQuery';

/**
 * Relationship picker (GIFTWEB-19), lifted from the OTC prototype:
 *  - ≥ 1024px: the native <select> with a chevron (SelectField)
 *  - < 1024px: a button showing the current value (or the placeholder) that
 *    opens a single-select bottom sheet titled "Relationship to {child}".
 * Both read the same `options` in the same order; the value is the API enum.
 */
export type RelationshipFieldProps = {
  id?: string;
  label: ReactNode;
  sheetTitle: string;
  options: readonly SelectOption[];
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  required?: boolean;
  error?: string;
  nudge?: boolean;
  className?: string;
};

const triggerClasses = (status: FieldStatus, hasValue: boolean) =>
  cn(
    'flex w-full cursor-pointer items-center justify-between gap-2.5 rounded-[13px] border-[1.5px] border-[#8b8a88] bg-white px-[15px] py-[15px]',
    'text-left text-base leading-snug font-medium',
    'outline-none transition-[border-color,box-shadow] duration-150',
    'focus:border-[#256956] focus:ring-[3px] focus:ring-[#256956]/20',
    hasValue ? 'text-[#1a201d]' : 'text-[#7a847d]',
    status === 'nudge' &&
      'border-[#66bda4] ring-4 ring-[#66bda4]/25 animate-[req-pulse_.5s_ease]',
    status === 'error' && 'border-[#f43f5e] bg-[#fff1f2]',
  );

function Tick() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#fff'
      strokeWidth='3.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M20 6L9 17l-5-5' />
    </svg>
  );
}

export const RelationshipField = forwardRef<
  HTMLSelectElement | HTMLButtonElement,
  RelationshipFieldProps
>(function RelationshipField(
  {
    id,
    label,
    sheetTitle,
    options,
    placeholder = 'Select relationship',
    value,
    onChange,
    onBlur,
    name,
    required,
    error,
    nudge,
    className,
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY, true);
  const [open, setOpen] = useState(false);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  if (isDesktop) {
    return (
      <SelectField
        ref={ref as React.Ref<HTMLSelectElement>}
        id={fieldId}
        name={name}
        label={label}
        placeholder={placeholder}
        options={options}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        error={error}
        nudge={nudge}
        className={className}
      />
    );
  }

  const status: FieldStatus = error ? 'error' : nudge ? 'nudge' : 'default';
  const selected = options.find((o) => o.value === value);
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  const pick = (next: string) => {
    onChange(next);
    setOpen(false); // single tap selects and closes (AC 4)
  };

  // Roving focus between the options with the arrow keys.
  const onOptionKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const last = options.length - 1;
    const go = (n: number) => {
      e.preventDefault();
      optionRefs.current[n]?.focus();
    };
    if (e.key === 'ArrowDown') go(i === last ? 0 : i + 1);
    else if (e.key === 'ArrowUp') go(i === 0 ? last : i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(last);
  };

  return (
    <FieldShell
      id={fieldId}
      label={label}
      required={required}
      error={error}
      nudge={nudge}
      className={className}
    >
      {(a11y) => (
        <Sheet
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) onBlur?.();
          }}
        >
          {/* Radix owns open/close and returns focus to this trigger (AC 7). */}
          <SheetTrigger asChild>
            <button
              ref={ref as React.Ref<HTMLButtonElement>}
              id={fieldId}
              name={name}
              type='button'
              className={triggerClasses(status, Boolean(selected))}
              data-testid={`${fieldId}-trigger`}
              data-value={value}
              {...a11y}
            >
              <span>{selected?.label ?? placeholder}</span>
              <Chevron className='shrink-0' />
            </button>
          </SheetTrigger>

          <SheetContent
            showCloseButton={false}
            className='gap-0 rounded-t-[26px] border-t-0 px-5 pt-3.5 shadow-[0_-18px_44px_-18px_rgba(20,40,32,0.55)]'
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              optionRefs.current[selectedIndex]?.focus();
            }}
          >
            <div className='relative mb-5 flex items-center justify-center'>
              <SheetTitle className='text-[17px] font-bold tracking-[-0.01em] text-[#1a201d]'>
                {sheetTitle}
              </SheetTitle>
              <VisuallyHidden>
                <SheetDescription>Choose one option</SheetDescription>
              </VisuallyHidden>
              <SheetClose asChild>
                <button
                  type='button'
                  aria-label='Close'
                  className='absolute top-1/2 right-0 flex size-[30px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-[#f3f1ea]'
                >
                  <svg
                    width='15'
                    height='15'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#6f6e6c'
                    strokeWidth='2.2'
                    strokeLinecap='round'
                    aria-hidden='true'
                  >
                    <path d='M18 6 6 18M6 6l12 12' />
                  </svg>
                </button>
              </SheetClose>
            </div>

            <div
              role='radiogroup'
              aria-label={sheetTitle}
              className='flex flex-col border-t border-[#f4f2ec]'
            >
              {options.map((o, i) => {
                const isSelected = o.value === value;
                return (
                  <button
                    key={o.value}
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    type='button'
                    role='radio'
                    aria-checked={isSelected}
                    tabIndex={i === selectedIndex ? 0 : -1}
                    onClick={() => pick(o.value)}
                    onKeyDown={(e) => onOptionKeyDown(e, i)}
                    data-testid={`${fieldId}-option-${o.value}`}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2.5 border-none px-3 py-[15px] text-left outline-none',
                      'focus-visible:ring-[3px] focus-visible:ring-[#256956]/30 focus-visible:ring-inset',
                      i < options.length - 1 && 'border-b border-[#f4f2ec]',
                      isSelected ? 'bg-[#eef3ef]' : 'bg-white',
                    )}
                  >
                    <span
                      className={cn(
                        'flex-1 text-[15px] text-[#2b322e]',
                        isSelected ? 'font-semibold' : 'font-normal',
                      )}
                    >
                      {o.label}
                    </span>
                    <span
                      aria-hidden='true'
                      className={cn(
                        'flex size-5 shrink-0 items-center justify-center rounded-sm border-2',
                        isSelected
                          ? 'border-[#256956] bg-[#256956]'
                          : 'border-[#c2c7c1] bg-white',
                      )}
                    >
                      {isSelected && <Tick />}
                    </span>
                  </button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </FieldShell>
  );
});

export default RelationshipField;
